import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Empty, Input, Modal, Radio, Space, Table, Tabs,
  Tag, Tooltip, Typography, message,
} from 'antd';
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import './AdminQuestionBank.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Банк вопросов, дубли и расхождения.
 *
 * Вопрос живёт копиями в разных тестах, и до сих пор его нельзя было найти,
 * не зная теста. Здесь поиск идёт по всем сразу, видно охват каждой
 * формулировки, а два вида проблем разведены: полный повтор внутри одного
 * теста и копии, которые расходятся в том, какой ответ считать верным.
 */

interface BankItem {
  question_id: number;
  text: string;
  type: string;
  copies: number;
  tests: number;
  has_conflict: boolean;
  test_id: number;
  sample_tests: string[];
}

interface DuplicateGroup {
  test_id: number;
  test_title: string;
  text: string;
  copies: number;
  answered: number;
  questions: Array<{ id: number; order: number; answered: boolean }>;
}

interface ConflictVariant {
  correct: string[];
  count: number;
  question_id: number;
  sample_tests: string[];
}

interface ConflictItem {
  question_id: number;
  text: string;
  copies: number;
  tests: number;
  options: string[];
  variants: ConflictVariant[];
}

const TYPE_LABEL: Record<string, string> = {
  single_choice: 'один ответ',
  multiple_choice: 'несколько',
  open_answer: 'открытый',
  matching: 'соответствие',
  image_identification: 'по фото',
};

const AdminQuestionBank: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('bank');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [bank, setBank] = useState<BankItem[]>([]);
  const [bankTotal, setBankTotal] = useState(0);
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [conflicts, setConflicts] = useState<ConflictItem[]>([]);

  const [search, setSearch] = useState('');
  const [repeatedOnly, setRepeatedOnly] = useState(true);
  const [conflictsOnly, setConflictsOnly] = useState(false);

  const [resolving, setResolving] = useState<ConflictItem | null>(null);
  const [chosenVariant, setChosenVariant] = useState<number | null>(null);

  const loadBank = useCallback(async () => {
    const { data } = await api.get<{ total: number; items: BankItem[] }>(
      '/admin/questions/bank',
      { params: { search: search || undefined, repeated_only: repeatedOnly, conflicts_only: conflictsOnly } },
    );
    setBank(data.items);
    setBankTotal(data.total);
  }, [search, repeatedOnly, conflictsOnly]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dups, confs] = await Promise.all([
        api.get<{ items: DuplicateGroup[] }>('/admin/questions/duplicates'),
        api.get<{ items: ConflictItem[] }>('/admin/questions/conflicts'),
      ]);
      setDuplicates(dups.data.items);
      setConflicts(confs.data.items);
      await loadBank();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось загрузить'));
    } finally {
      setLoading(false);
    }
  }, [loadBank]);

  // Намеренно один раз при открытии: loadAll пересоздаётся вместе с фильтрами,
  // а перезагружать всё на каждый символ в поиске не нужно — для банка ниже
  // есть отдельный эффект с задержкой.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll(); }, []);
  useEffect(() => {
    const timer = setTimeout(() => { loadBank().catch(() => {}); }, 250);
    return () => clearTimeout(timer);
  }, [loadBank]);

  const merge = async (group: DuplicateGroup, keepId: number) => {
    setBusy(true);
    try {
      const { data } = await api.post('/admin/questions/duplicates/merge', {
        keep_question_id: keepId,
        remove_question_ids: group.questions.filter((q) => q.id !== keepId).map((q) => q.id),
      });
      message.success(`Убрано повторов: ${data.removed}`);
      await loadAll();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось слить'));
    } finally {
      setBusy(false);
    }
  };

  const resolve = async () => {
    if (!resolving || chosenVariant === null) return;
    setBusy(true);
    try {
      const { data } = await api.post('/admin/questions/conflicts/resolve', {
        question_id: chosenVariant,
      });
      message.success(data.aligned
        ? `Выровнено копий: ${data.aligned} в ${data.tests} тестах`
        : 'Все копии уже согласованы');
      setResolving(null);
      await loadAll();
    } catch (error) {
      message.error(getApiErrorMessage(error, 'не удалось выровнять'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout wide>
      <div data-guide="question-bank-home" className="qb-page">
        <div className="qb-head">
          <div>
            <Title level={3} style={{ marginBottom: 2 }}>Банк вопросов</Title>
            <Text type="secondary">
              Поиск по всем вопросам сразу, с охватом и разбором проблем.
            </Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={loadAll} loading={loading}>Обновить</Button>
        </div>

        <Tabs data-guide="question-bank-tabs"
          activeKey={tab} onChange={setTab}
          items={[
            {
              key: 'bank',
              label: 'Все вопросы',
              children: (
                <>
                  <Space wrap style={{ marginBottom: 12 }}>
                    <Input
                      allowClear prefix={<SearchOutlined />} style={{ width: 340 }}
                      placeholder="Текст вопроса…"
                      value={search} onChange={(e) => setSearch(e.target.value)}
                    />
                    <Button
                      type={repeatedOnly ? 'primary' : 'default'} size="small"
                      onClick={() => setRepeatedOnly((v) => !v)}
                    >
                      Только повторяющиеся
                    </Button>
                    <Button
                      danger={conflictsOnly} type={conflictsOnly ? 'primary' : 'default'} size="small"
                      onClick={() => setConflictsOnly((v) => !v)}
                    >
                      С расхождением
                    </Button>
                    <Text type="secondary">Найдено {bankTotal}</Text>
                  </Space>
                  <Table<BankItem>
                    rowKey="question_id" size="small" loading={loading} dataSource={bank}
                    pagination={{ pageSize: 25, showSizeChanger: true }}
                    columns={[
                      {
                        title: 'Вопрос', dataIndex: 'text', key: 'text',
                        render: (text: string, row) => (
                          <div>
                            <div className="qb-question-text">{text.trim()}</div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {row.sample_tests.slice(0, 2).join(' · ')}
                            </Text>
                          </div>
                        ),
                      },
                      {
                        title: 'Тип', dataIndex: 'type', key: 'type', width: 130,
                        render: (value: string) => (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {TYPE_LABEL[value] || value}
                          </Text>
                        ),
                      },
                      {
                        title: 'Тестов', dataIndex: 'tests', key: 'tests', width: 90,
                        sorter: (a, b) => a.tests - b.tests,
                        render: (value: number) => <b>{value}</b>,
                      },
                      { title: 'Копий', dataIndex: 'copies', key: 'copies', width: 90 },
                      {
                        title: 'Состояние', key: 'state', width: 190,
                        render: (_: unknown, row) => (row.has_conflict
                          ? <Tag color="red">разный правильный ответ</Tag>
                          : <Tag color="green">согласован</Tag>),
                      },
                      {
                        title: '', key: 'actions', width: 170,
                        render: (_: unknown, row) => (row.has_conflict ? (
                          <Space size={4}>
                          <Button size="small" onClick={() => navigate(
                            `/admin/board?test=${row.test_id}&tab=questions`)}>Тест</Button>
                          <Button size="small" type="primary" ghost onClick={() => {
                            const item = conflicts.find(
                              (c) => c.text.trim().toLowerCase() === row.text.trim().toLowerCase());
                            if (item) { setResolving(item); setChosenVariant(item.variants[0].question_id); setTab('conflicts'); }
                          }}>Разобрать</Button>
                          </Space>
                        ) : (
                          <Button size="small" onClick={() => navigate(
                            `/admin/board?test=${row.test_id}&tab=questions`)}>
                            Открыть тест
                          </Button>
                        )),
                      },
                    ]}
                  />
                  <Alert
                    type="info" showIcon style={{ marginTop: 12 }}
                    message="Повторение вопроса между тестами — это норма, так переиспользуют материал. Проблема — только когда копии расходятся в правильном ответе."
                  />
                </>
              ),
            },
            {
              key: 'duplicates',
              label: <Badge count={duplicates.length} offset={[10, -2]} size="small">
                <span style={{ paddingRight: 14 }}>Дубли в тесте</span></Badge>,
              children: (
                <>
                  <Paragraph type="secondary">
                    Полный повтор внутри одного теста: совпадают текст, картинка и все
                    варианты ответа. Сотрудник видит такой вопрос дважды. Вопросы с
                    одинаковым текстом, но разными фото, сюда не попадают — это разные
                    вопросы.
                  </Paragraph>
                  {!duplicates.length && !loading && (
                    <Empty description="Дублей нет" style={{ padding: '40px 0' }} />
                  )}
                  {duplicates.map((group) => (
                    <Card key={`${group.test_id}-${group.questions[0].id}`}
                      size="small" className="qb-group">
                      <div className="qb-group__head">
                        <Space size={8} wrap>
                          <Tag color={group.answered ? 'gold' : 'green'}>
                            {group.answered ? `есть ответы: ${group.answered}` : 'без истории'}
                          </Tag>
                          <Tag>×{group.copies}</Tag>
                          <Tag color="blue">{group.test_title}</Tag>
                        </Space>
                        <Tooltip title={group.answered
                          ? 'По копиям уже отвечали — удаление исказит баллы. Разбирайте вручную в редакторе теста.'
                          : undefined}>
                          <Button
                            size="small" type="primary" disabled={busy || group.answered > 0}
                            onClick={() => merge(group, group.questions[0].id)}
                          >
                            Оставить первую, остальные убрать
                          </Button>
                        </Tooltip>
                      </div>
                      <div className="qb-group__text">{group.text.trim()}</div>
                      <Space size={6} wrap>
                        {group.questions.map((q, index) => (
                          <Tag key={q.id} color={index === 0 ? 'green' : undefined}>
                            #{q.id}{index === 0 ? ' · эталон' : ''}{q.answered ? ' · есть ответы' : ''}
                          </Tag>
                        ))}
                      </Space>
                    </Card>
                  ))}
                </>
              ),
            },
            {
              key: 'conflicts',
              label: <Badge count={conflicts.length} offset={[10, -2]} size="small">
                <span style={{ paddingRight: 14 }}>Расхождения</span></Badge>,
              children: (
                <>
                  <Paragraph type="secondary">
                    У этих копий совпадают формулировка и все варианты ответа, но верным
                    отмечено разное. За идентичный ответ сотрудников оценивают по-разному.
                  </Paragraph>
                  {!conflicts.length && !loading && (
                    <Empty description="Расхождений нет" style={{ padding: '40px 0' }} />
                  )}
                  {conflicts.map((item) => (
                    <Card key={item.question_id} size="small" className="qb-group">
                      <div className="qb-group__head">
                        <Space size={8} wrap>
                          <Tag color="red">расхождение</Tag>
                          <Tag>{item.tests} тестов · {item.copies} копий</Tag>
                        </Space>
                        <Button size="small" type="primary" disabled={busy}
                          onClick={() => { setResolving(item); setChosenVariant(item.variants[0].question_id); }}>
                          Выбрать верный и выровнять
                        </Button>
                      </div>
                      <div className="qb-group__text">{item.text.trim()}</div>
                      <div className="qb-variants">
                        {item.variants.map((variant, index) => (
                          <div key={variant.question_id} className="qb-variant">
                            <div className="qb-variant__head">
                              Вариант {index + 1}
                              <Tag color={index === 0 ? 'blue' : undefined}
                                style={{ marginLeft: 6 }}>{variant.count} копий</Tag>
                            </div>
                            <ul className="qb-options">
                              {item.options.map((option) => (
                                <li key={option}
                                  className={variant.correct.includes(option) ? 'is-correct' : ''}>
                                  {option}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </Card>
                  ))}
                </>
              ),
            },
          ]}
        />
      </div>

      <Modal
        open={!!resolving}
        title="Какой вариант считать верным"
        onCancel={() => setResolving(null)}
        onOk={resolve}
        okText="Выровнять все копии"
        okButtonProps={{ danger: true, loading: busy, disabled: chosenVariant === null }}
        width={660}
      >
        {resolving && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Text strong>{resolving.text.trim()}</Text>
            <Radio.Group
              value={chosenVariant}
              onChange={(e) => setChosenVariant(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {resolving.variants.map((variant, index) => (
                  <Radio key={variant.question_id} value={variant.question_id}
                    className="qb-radio">
                    <div>
                      <b>Вариант {index + 1}</b> · сейчас так отмечено в {variant.count} копиях
                      <ul className="qb-options">
                        {resolving.options.map((option) => (
                          <li key={option}
                            className={variant.correct.includes(option) ? 'is-correct' : ''}>
                            {option}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            <Alert
              type="warning" showIcon
              message={`Отметки правильного ответа во всех ${resolving.copies} копиях станут такими же. Завершённые попытки будут пересчитаны — баллы у части сотрудников изменятся.`}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Вопросы с тем же текстом, но другим набором вариантов или другим фото,
              не затрагиваются.
            </Text>
          </Space>
        )}
      </Modal>
    </Layout>
  );
};

export default AdminQuestionBank;
